'use client';

import Link from 'next/link';
import {
  BREED_HANDBOOK_FIT_CHECK_CTA,
  breedHandbookFitCheckPath,
  myBreedHandbookQuestionsPath
} from '@paction-2.0/util/constants/breedHandbooks';
import { withIndefiniteArticle } from '@paction-2.0/util/formatters';

import { trackHandbookEvent } from './handbookEvents';
import styles from './handbookWaitlistPrompt.module.scss';
import { useBreedHandbooksEnabled } from './useBreedHandbooksEnabled';
import { useHandbookEventsViewer } from './useHandbookEventsViewer';
import { useOwnsHandbook } from './useOwnsHandbook';
import { useTrackWhenVisible } from './useTrackWhenVisible';

type HandbookWaitlistPromptProps = {
  breedName: string;
  breedSlug: string;
  surface?: 'waitlist_widget' | 'messages';
  title?: string;
};

export function HandbookWaitlistPrompt({
  breedName,
  breedSlug,
  surface = 'waitlist_widget',
  title = 'While we find your breeders'
}: HandbookWaitlistPromptProps) {
  useHandbookEventsViewer();
  const { isEnabled } = useBreedHandbooksEnabled();
  const ownsHandbook = useOwnsHandbook(breedSlug);
  const promptRef = useTrackWhenVisible<HTMLDivElement>(
    isEnabled ? `${surface}:${breedSlug}` : null,
    () =>
      trackHandbookEvent('handbook_ad_view', {
        surface,
        breed_slug: breedSlug,
        owned: ownsHandbook
      })
  );
  if (!isEnabled) return null;

  return (
    <div ref={promptRef} className={styles.prompt}>
      <p className={styles.title}>{title}</p>
      <p className={styles.body}>
        {ownsHandbook
          ? `Your ${breedName} guide has the questions to ask breeders before you pay a deposit.`
          : `Get ${withIndefiniteArticle(breedName)} guide personalized to your home, with what to ask breeders before you pay a deposit.`}
      </p>
      <Link
        href={
          ownsHandbook
            ? myBreedHandbookQuestionsPath(breedSlug)
            : breedHandbookFitCheckPath(breedSlug)
        }
        className={styles.link}
        onClick={() =>
          trackHandbookEvent('handbook_ad_click', {
            surface,
            breed_slug: breedSlug,
            owned: ownsHandbook,
            cta: ownsHandbook ? 'open' : 'preview'
          })
        }
      >
        {ownsHandbook
          ? 'Open my question script'
          : BREED_HANDBOOK_FIT_CHECK_CTA}
      </Link>
    </div>
  );
}
